import React, { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircleCheck, CircleX, FileCheck, Info as InfoIcon } from 'lucide-react';
import { withRouter } from '../../WithRouter';
import { getCurrentProfile } from '../../../api/cuf';
import { appStatuses, urls } from '../../../constants';
import { sections } from '../../../constants/sections';
import { compareProfiles } from '../../../utils/compare';
import { AuthContext } from '../../../context/Auth.context';
import { ProfileContext } from '../../../context/Profile.context';
import Title from '../../Title';
import Loading from '../../Loading';
import { ReviewSection } from '../../ReviewSection';
import { Box, Button, Card, CardContent, CardHeader, CardTitle, Divider, InfoPanel, TextField, Tooltip, Typography } from '@otcmarketsgroup/otcm-ui-lib';

const ReviewSubmit = ({ params: { symbol }, handleSubmit, isSubmitting }) => {
  const [authState] = useContext(AuthContext);
  const [profileState, dispatchProfile] = useContext(ProfileContext);
  const [totalSections] = useState(sections.length - 1);
  const [totalCompleted, setTotalCompleted] = useState(null);
  const [isCurrentLoaded, setCurrentLoaded] = useState(false);
  const [isCurrentError, setCurrentError] = useState(null);
  const [compareJson, setCompareJson] = useState(null);
  const profileData = profileState.profile;
  const currentData = profileState.currentProfile;
  const verifiedSections = profileData.verifiedSections;
  const isVerified = profileState.verifiedProfile;
  const isInProgress = profileData.status.id === appStatuses.IN_PROGRESS;
  const allSectionsVerified = totalCompleted === totalSections;
  const allVerifyActive = Object.keys(profileState.enableVerifiedSections).every(key => profileState.enableVerifiedSections[key] === true);
  const submitCta = allSectionsVerified ? 'Submit Verified Profile' : 'Submit Update';
  const submitType = allSectionsVerified ? 'verify' : 'update';
  const userEmail = authState?.user?.email;
  const isReadOnly = profileState.readOnly;

  useEffect(() => {
    if (!currentData) {
      const currentParams = {
        symbol,
        email: userEmail
      };

      getCurrentProfile(currentParams)
        .then(data => {
          if (data) setCurrentLoaded(true);

          dispatchProfile({
            type: 'SET_CURRENT_PROFILE',
            payload: data
          });
        })
        .catch(err => {
          const isStatus504 = err?.response?.data?.code === 504;
          setCurrentError(`Error loading data.${isStatus504 ? ' Please wait a few minutes and try again.' : ''}`);
        });
    } else {
      setCompareJson(compareProfiles(currentData, profileData));
      setCurrentLoaded(true);
    }
  }, [dispatchProfile, currentData, profileData, symbol, userEmail]);

  useEffect(() => {
    if (verifiedSections) {
      let completedCount = 0;

      for (let property in verifiedSections) {
        if (property !== 'allVerified' && verifiedSections[property]) completedCount++;
      }

      setTotalCompleted(completedCount);
    }
  }, [verifiedSections]);

  const onValueChanged = e => {
    dispatchProfile({
      type: 'UPDATE_PROFILE',
      field: 'questionComment',
      payload: e.target.value
    });
  };

  const onSubmitClick = () => {
    let compareDiff = { ...compareJson };
    const authorizedUsers = profileData.peopleList.filter(person => person.isAuthorizedUser);
    const newAuthorizedUsers = compareJson.designatedContacts.filter(person => person.isNew
      && person.fields && person.fields.find(field => field.fieldName === 'OTCIQ User' && field.newValue === 'Yes'));

    if (!allVerifyActive && isVerified) compareDiff = { ...compareDiff, removeVerify: true };
    if (!authorizedUsers || authorizedUsers.length === 0) compareDiff = { ...compareDiff, noAuthorizedUsers: true };
    if (newAuthorizedUsers && newAuthorizedUsers.length >= 1) compareDiff = { ...compareDiff, backgroundcheckMissing: true };

    handleSubmit(submitType, compareDiff);
  };

  return <Box display='flex' flexDirection='column' gap={2}>
    <Card>
      <CardHeader>
        <CardTitle icon={<FileCheck />}>Review and Submit</CardTitle>
      </CardHeader>
      <CardContent>
        <Loading loaded={isCurrentLoaded} error={isCurrentError}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allSectionsVerified && <InfoPanel
              title={`${totalCompleted} out of ${totalSections} sections have been verified`}
              icon={<CircleCheck size={20} color='var(--otc-green)' />}
            >
              <Typography variant='body1'>
                Submit the changes displayed on this page to verify company profile for {profileData.companyInfo.companyName}.
              </Typography>
            </InfoPanel>}
            {!allSectionsVerified && <InfoPanel
              isWarning
              title={`Only ${totalCompleted} out of ${totalSections} sections have been verified`}
              icon={<CircleX size={20} color='var(--otc-warning)' />}
            >
              <Typography variant='body1'>
                {(isVerified && !allVerifyActive)
                  ? 'Your changes will be processed, but your profile will no longer be verified because you are missing required information. Maintaining a Verified Profile is required for OTCQX, OTCQB, OTCID, or Pink Limited.'
                  : 'Your changes will be processed, but your profile will not be verified — maintaining a Verified Profile is required for OTCQX, OTCQB, OTCID, or Pink Limited.'}
              </Typography>
            </InfoPanel>}
            {compareJson && sections.map(section => {
              let path = section.path;

              if (section.tabs) path = `${path}/${section.tabs[0]}`;
              if (section.static) return;

              return !section.static && <ReviewSection key={section.path} identifier={section.key} name={section.name} symbol={symbol} data={compareJson} path={path} isVerified={verifiedSections[section.key]} />;
            })}
            {isInProgress && <>
              <Card hasBorder>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Title title='Questions & Comments' type='h2' />
                  <Tooltip
                    dark
                    subtitle='Feel free to add any additional information that OTC Markets would need to process your Company Profile changes.'
                  >
                    <InfoIcon size={14} color='var(--otc-green)' />
                  </Tooltip>
                </Box>
                <TextField
                  multiline
                  value={profileData.questionComment}
                  onChange={onValueChanged}
                  placeholder='Enter text here.'
                  showCharCounter
                  minRows={2}
                  maxLength={1500}
                />
              </Card>
              <Typography variant='body1'>
                By submitting this profile update, I agree to the terms stated within OTC Markets Group's <Box component="span" sx={{ color: "var(--otc-green)", fontWeight: 700, ":hover": { textDecoration: "underline" } }}><a href={urls.PRIVACY_POLICY} target='_blank' rel='noopener noreferrer'>Privacy Policy Statement</a></Box> with regards to use of personal data.
              </Typography>
            </>}
          </Box>
        </Loading>
      </CardContent>
    </Card>
    {!isReadOnly && <Button fullWidth disabled={(!isCurrentLoaded || isSubmitting)} isLoading={isSubmitting} onClick={onSubmitClick}>
      {submitCta}
    </Button>}
  </Box>;
};

ReviewSubmit.propTypes = {
  params: PropTypes.shape({
    symbol: PropTypes.string
  }),
  handleSubmit: PropTypes.func
};

export default withRouter(ReviewSubmit);
